import React from 'react';
import { Alert, Box, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Container } from '@mui/material';
import { PageUrl } from 'const/page-url';
import { Link as RouterLink } from 'react-router-dom';

// 本文は交付用書面(ohana/aloha: pdf/契約時/_src/07_外部AI事業者への個人情報の送信に関する説明書.html)と
// 同一の内容。法的な情報提供文書のため、文言を改変せずWeb向けの体裁のみ変更している。
// 改定する場合は交付用書面と同時に改定し、版・日付を一致させること。
const CONTACT_EMAIL = 'info@cocowa-tech.com';
const DOCUMENT_VERSION = '第1.0版　2026年9月10日';

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        variant="h5"
        gutterBottom
        sx={{ borderLeft: '4px solid', borderColor: 'grey.700', pl: 1.5, fontSize: { xs: '1.05rem', md: '1.25rem' } }}
    >
        {children}
    </Typography>
);

const QuoteBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="body2" component="div" sx={{ lineHeight: 1.9 }}>
            {children}
        </Typography>
    </Paper>
);

const AiDataTransferContent: React.FC = () => {
    return (
        <Container maxWidth="md" sx={{ p: 5 }}>
            <Alert severity="info" sx={{ mb: 4 }}>
                <Typography variant="body2" paragraph sx={{ fontWeight: 700, mb: 1 }}>
                    このページについて
                </Typography>
                <Typography variant="body2" sx={{ mb: 0 }}>
                    本ページは、OHANA AI をご契約の事業所にお渡ししている「外部AI事業者への個人情報の送信に関する説明書」と同一の内容を掲載するものです。
                    個人情報の保護に関する法律施行規則第18条第3項に基づくご本人への情報提供を、同条第2項が認める方法により常時行うために公開しています。
                    ご利用中の事業所の方、および事業所を通じて説明をお求めの保護者の方は、本ページをそのままご参照ください。ご不明な点は末尾のお問い合わせ先までご連絡ください。
                </Typography>
            </Alert>

            <Typography variant="body1" paragraph>
                株式会社Cocowa（以下「当社」）は、OHANA AI サービス利用契約書第7条に基づき、貴事業所が本サービスに入力される個人情報の取扱いについて、次のとおりご説明します。
                本書は、貴事業所が個人情報の安全管理措置として外国での取扱いを把握し、保護者等へ説明・公表される際の資料としてご利用いただけます。
            </Typography>

            <Box mb={4}>
                <SectionHeading>1. 情報の保管場所</SectionHeading>
                <Typography variant="body1" paragraph>
                    貴事業所が本サービスに入力した情報（児童等の氏名、支援計画、活動記録等）は、Amazon Web Services の日本国内（東京リージョン）のサーバーに保管されます。データベースに保存される情報は、保存時に暗号化されています。
                </Typography>
            </Box>

            <Box mb={4}>
                <SectionHeading>2. AI機能で外部AI事業者へ送信される情報</SectionHeading>
                <Typography variant="body1" paragraph>
                    支援計画の下書き作成、アセスメント、報告書作成、音声入力等のAI機能をご利用の際、次の情報が当社のサーバーを経由して外部AI事業者へ送信されます。
                </Typography>
                <ul>
                    <li>
                        <strong>すべてのAI機能に共通：</strong>
                        職員が入力した自由記述、面談・会議の記録本文、目標・支援内容の文章、領域・カテゴリ等の選択内容。
                        <strong>氏名等の匿名化処理は行っていないため、自由記述に氏名等を記載された場合はそのまま送信されます。</strong>
                    </li>
                    <li>
                        <strong>個人を直接識別できる情報を送信する機能：</strong>
                        (a) 既存の支援計画書の写真取り込み（書面の画像そのもの）、(b) 音声入力（録音した音声とその文字起こし）。
                    </li>
                    <li>
                        <strong>送信しない情報：</strong>
                        上記(a)(b)を除き、児童等の氏名・生年月日・受給者証番号・保護者の氏名をAI機能の入力に含める仕組みはありません。不登校支援の月次報告書の生成では、児童等の氏名・担当職員の氏名・事業所名を外部AI事業者へ送信せず、本文中は「本人」と表記し、末尾の署名は当社サーバー側で付加しています。職員のログイン情報・パスワードは送信しません。
                    </li>
                </ul>
            </Box>

            <Box mb={4}>
                <SectionHeading>3. 送信先の事業者と所在国</SectionHeading>
                <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: '42%' }}>事業者</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '14%' }}>所在国</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>用途</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>OpenAI OpCo, LLC およびその関連会社</TableCell>
                                <TableCell>米国</TableCell>
                                <TableCell>文章生成、要約、画像からの文字読取、音声認識</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Anthropic, PBC およびその関連会社</TableCell>
                                <TableCell>米国</TableCell>
                                <TableCell>対話型アセスメントおよびその集約、単一ラウンドアセスメント（保育所等訪問支援）の提案生成</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box mb={4}>
                <SectionHeading>4. 移転先の国（米国）における個人情報の保護に関する制度</SectionHeading>
                <Typography variant="body1" paragraph>
                    米国には、個人情報の保護に関する包括的な連邦法はなく、分野別の連邦法および州法により規律されています。詳細は、個人情報保護委員会が公表する「外国における個人情報の保護に関する制度等の調査」の米国の項をご参照ください。
                </Typography>
            </Box>

            <Box mb={4}>
                <SectionHeading>5. 送信先の事業者が講ずる措置</SectionHeading>
                <Typography variant="body1" paragraph>
                    当社は、上記の事業者が提供する事業者向けAPIを、各社のデータ処理条件のもとで利用しています。各社は、事業者向けAPI経由で送信された情報を、当社への役務提供の目的以外に利用しないこと、および自社のAIモデルの学習に利用しないことを、利用規約またはデータ処理条件において定めています。送信された情報は、不正利用の監視等の目的で各社において一定期間（既定では最長30日間）保持された後に削除されます。
                    <strong>ただし、法令上の保持義務がある場合、および各社の利用ポリシーへの違反が検知された場合は、これを超えて保持されることがあります</strong>
                    （Anthropicは、違反が検知された場合の入出力を最長2年間、判定のスコアを最長7年間保持するとしています）。当社は、本書の版の時点で、各社との間で保持期間をゼロとする追加契約を締結していません。
                </Typography>
            </Box>

            <Box mb={4}>
                <SectionHeading>6. AI機能以外の外部送信について</SectionHeading>
                <Typography variant="body1" paragraph>
                    本サービスは、障害発生時の通知および追加契約のお申込みの通知のため、通知先として Telegram（Telegram Messenger Inc.）を利用しています。当該通知には、事業所名が含まれることがあります。職員の氏名、メールアドレス、接続元IPアドレスは、送信前に利用者IDまたは伏字に置き換えており、同社へ送信していません。同社の情報の保存国は公表されていません。詳細は当社プライバシーポリシーに記載しています。
                </Typography>
            </Box>

            <Box mb={4}>
                <SectionHeading>7. 米国への移転についての当社の対応</SectionHeading>
                <Typography variant="body1" paragraph>
                    米国の事業者へ個人データを移転するにあたり、
                    <strong>当社が個人情報保護法第28条に基づく対応を行います。</strong>
                    当社は、上記5項の事業者との間で、個人情報取扱事業者が講ずべき措置に相当する措置が継続的に講じられる体制を確保したうえで移転しています（同条第1項、同法施行規則第16条第1号）。具体的には次のとおりです。
                </Typography>
                <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: '34%' }}>項目</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>内容</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>体制の整備の方法</TableCell>
                                <TableCell>各社との間のデータ処理に関する契約条件により、当社の指示の範囲でのみ処理すること、モデルの学習に利用しないこと、安全管理措置、再委託先の監督、契約終了時のデータの返却または削除等を定めています。</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>移転先の国の名称</TableCell>
                                <TableCell>アメリカ合衆国（上記3項・4項）</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>確認の頻度および方法</TableCell>
                                <TableCell>
                                    <strong>年1回以上</strong>
                                    、上記契約の履行状況を各社への照会または各社が公表する情報の確認により確認し、あわせて相当措置の実施に影響を及ぼすおそれのある米国の制度の有無および内容を、個人情報保護委員会が公表する調査資料等により確認します。
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>支障の有無</TableCell>
                                <TableCell>本書の版の時点において、相当措置の実施に支障は生じていません。</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>支障が生じた場合の措置</TableCell>
                                <TableCell>是正を求めるとともに必要かつ適切な措置を講じ、相当措置の継続的な実施の確保が困難となったときは、当該事業者への提供を停止します。</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="body1" paragraph>
                    本人（児童等の保護者等）から求めがあった場合、当社は、上記の情報を遅滞なく提供します（お問い合わせ先は本書末尾）。貴事業所を経由してご連絡いただいても構いません。
                </Typography>
                <Typography variant="body1" paragraph>
                    したがって、
                    <strong>本サービスの利用にあたって、貴事業所が保護者等から「米国への移転」について個別に同意を取得していただく必要はありません。</strong>
                    貴事業所には、次の3点をお願いします。
                </Typography>
            </Box>

            <Box mb={4}>
                <SectionHeading>8. 貴事業所にお願いする事項</SectionHeading>
                <ul>
                    <li>
                        <strong>(1) 利用目的の範囲の確認。</strong>
                        上記の取扱いが、貴事業所が保護者等に通知・公表しておられる利用目的（個別支援計画の作成、支援の記録等）の範囲内であることをご確認ください。範囲内であれば、新たな同意の取得は不要です。
                    </li>
                    <li>
                        <strong>(2) 公表事項への記載。</strong>
                        貴事業所のプライバシーポリシー、個人情報保護方針または重要事項説明書に、記録の管理を外部に委託していることを記載してください。個人情報保護委員会のガイドラインQ&A（10-25）は、安全管理措置として
                        <strong>「委託先が所在する外国の名称」と「サーバーが所在する外国の名称」の両方</strong>
                        を明らかにすることを求めています。9項の文例をそのままお使いいただけます。
                    </li>
                    <li>
                        <strong>(3) 入力内容へのご配慮。</strong>
                        AI機能の対象となる自由記述等には、支援上必要な範囲を超える個人情報（きょうだい、他の利用者、学校名等）を記載しないようご配慮ください。
                    </li>
                </ul>
                <Typography variant="body1" paragraph>
                    なお、貴事業所の判断で保護者等から個別に同意を取得されることを妨げるものではありません。自治体の指導や既存の同意書の様式上、同意を取得される場合の文例も9項に示します。
                </Typography>
            </Box>

            <Box mb={4}>
                <SectionHeading>9. 記載文例</SectionHeading>
                <Typography variant="body1" paragraph sx={{ fontWeight: 700 }}>
                    (A) 公表事項・重要事項説明書への記載（標準）
                </Typography>
                <QuoteBlock>
                    当事業所は、個別支援計画の作成や日々の記録の管理に、株式会社Cocowaが提供するクラウドシステム「OHANA AI」を利用しています。
                    支援計画の下書き作成等の際には、記録の一部（職員が入力した支援に関する文章。一部の機能ではお子さまのお名前を含みます）が、AI事業者において処理されます。
                    <strong>委託先および再委託先が所在する国: アメリカ合衆国</strong>
                    （Amazon Web Services, Inc.、OpenAI社、Anthropic社）。
                    <strong>記録が保存されるサーバーの所在国: 日本</strong>
                    （AI処理の際に送信される情報は、米国のサーバーで処理されます）。
                    米国には日本の個人情報保護法に相当する包括的な法律はありませんが、株式会社Cocowaは、これらの事業者との間で、日本の個人情報取扱事業者が講ずべき措置に相当する措置が継続的に講じられる体制を確保しています。
                    取扱いの詳細については、当事業所までお問い合わせください。
                </QuoteBlock>
                <Typography variant="body1" paragraph sx={{ fontWeight: 700 }}>
                    (B) 保護者等から個別に同意を取得される場合（任意）
                </Typography>
                <QuoteBlock>
                    上記(A)の文面に続けて、「この取扱いについて、ご同意をお願いします。」等の一文を加えてご利用ください。
                </QuoteBlock>
            </Box>

            <Box sx={{ borderTop: '1px solid', borderColor: 'grey.400', pt: 3, mt: 6 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                    本書に関するお問い合わせ: 株式会社Cocowa　
                    <Link href={`mailto:${CONTACT_EMAIL}`} underline="hover">
                        {CONTACT_EMAIL}
                    </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    {DOCUMENT_VERSION}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    当社の個人情報の取扱い全般については
                    <Link component={RouterLink} to={PageUrl.PrivacyPolicy.urlWithSlash} underline="hover">
                        プライバシーポリシー
                    </Link>
                    をご覧ください。
                </Typography>
            </Box>
        </Container>
    );
};

export default AiDataTransferContent;
